/*
 *   File : basket-config.js
 *   Author : https://evoqins.com
 *   Description : basket config component (API integrated)
 *   Integrations : manage-basket/list, manage-basket/list-mapping, manage-basket/update-mapping
 *   Version : 2.2.0
 */
import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Tabs } from "../../../Components/Navigation";
import Icon from "../../../Components/icons";
import { Input, SelectBasket } from "../../../Components/FormElements";
import { BasketCard } from "../../../Components/Cards";
import { PrimaryButton } from "../../../Components/Buttons";
import { APIService } from "../../../Service";
import { toast } from "react-toastify";

export default function BasketConfig() {
  /* ----------------------------------------
      TAB STATE
  ---------------------------------------- */
  const [riskTabs, setRiskTabs] = useState([]);
  const [tenureTabs, setTenureTabs] = useState([]);

  const [activeRiskTab, setActiveRiskTab] = useState(null);
  const [activeTenureTab, setActiveTenureTab] = useState(null);

  /* ----------------------------------------
      BASKET SELECTION STATE
  ---------------------------------------- */
  const [riskSelectedBasket, setRiskSelectedBasket] = useState(null);
  const [riskSelectedBasketError, setRiskSelectedBasketError] = useState(null);

  const [tenureSelectedBasket, setTenureSelectedBasket] = useState(null);
  const [tenureSelectedBasketError, setTenureSelectedBasketError] = useState(null);

  /* ----------------------------------------
      SEARCH STATE
  ---------------------------------------- */
  const [riskSearchQuery, setRiskSearchQuery] = useState("");
  const [tenureSearchQuery, setTenureSearchQuery] = useState("");

  /* ----------------------------------------
      DATA STATE
  ---------------------------------------- */
  const [baskets, setBaskets] = useState([]);

  // Ref always holds the latest basket list so async callbacks
  // can look up baskets without stale closure state.
  const basketsRef = useRef([]);

  // Parks mapping response if it arrives before the basket list is ready.
  const pendingMappingRef = useRef(null);

  const [tabsLoading, setTabsLoading] = useState(true);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  /* ----------------------------------------
      INIT
  ---------------------------------------- */
  useEffect(() => {
    _getTabData();
    _getBaskets();
  }, []);

  // Whenever both active tabs are set, fetch the mapping
  useEffect(() => {
    if (activeRiskTab?.id && activeTenureTab?.id) {
      _getMapping(activeRiskTab.id, activeTenureTab.id);
    }
  }, [activeRiskTab, activeTenureTab]);

  // Clear errors on selection
  useEffect(() => {
    if (riskSelectedBasket?.id) setRiskSelectedBasketError(null);
  }, [riskSelectedBasket]);

  useEffect(() => {
    if (tenureSelectedBasket?.id) setTenureSelectedBasketError(null);
  }, [tenureSelectedBasket]);

  /* ----------------------------------------
      API – tab filter data
  ---------------------------------------- */
  const _getTabData = () => {
    setTabsLoading(true);
    APIService(false, "goal/filter-data")
      .then((res) => {
        if (res.status_code === 200) {
          const { investment_style = [], tenure = [] } = res.data;

          const mappedRisk = investment_style.map((item) => ({
            id: item.value,
            label: item.label.toLowerCase().replace(/\s+/g, ""),
            name: item.label,
          }));

          const mappedTenure = tenure.map((item) => ({
            id: item.value,
            label: item.label,
            name: item.label,
          }));

          setRiskTabs(mappedRisk);
          setTenureTabs(mappedTenure);

          // Setting defaults triggers the mapping fetch via useEffect
          if (mappedRisk.length > 0) setActiveRiskTab(mappedRisk[0]);
          if (mappedTenure.length > 0) setActiveTenureTab(mappedTenure[0]);
        }
      })
      .catch(() => {
        setRiskTabs([]);
        setTenureTabs([]);
      })
      .finally(() => setTabsLoading(false));
  };

  /* ----------------------------------------
      API – full basket list
  ---------------------------------------- */
  const mapApiToOption = (item) => {
    const cagr =
      item.return_percentage === "--" || item.return_percentage === null
        ? null
        : Number(item.return_percentage);

    return {
      // Coerce to Number so ID comparisons are type-safe
      id: Number(item.basket_style_id),
      img: item.image,
      title: item.name,
      CAGR: cagr,
      best_for: item.best_for || item.return_year || "",
      risk: item.risk || "",
      raw: item,
      return_year: item.return_year || "5Y",
    };
  };

  /**
   * Resolves a basket ID (number, string, or object with basket_style_id/id)
   * to a basket from the list. Coerces both sides to Number so "11" === 11.
   */
  const _findBasket = (rawId, basketList) => {
    if (rawId === null || rawId === undefined || rawId === "") return null;

    // If the mapping returns objects instead of plain IDs, unwrap them
    const id =
      typeof rawId === "object"
        ? Number(rawId.basket_style_id ?? rawId.id)
        : Number(rawId);

    return basketList.find((b) => Number(b.id) === id) ?? null;
  };

  /**
   * Given a mapping API response and the full basket list,
   * picks the best candidate for each slot and preselects it.
   *
   * Response shape: { risk_baskets: [id, ...], tenure_baskets: [id, ...] }
   * Recommended fields may or may not be present — always fall back to [0].
   */
  const _applyMapping = (mappingData, basketList) => {
    // ---- risk ----
    const riskCandidateId =
      mappingData.recommended_risk_basket ??
      mappingData.risk_baskets?.[0] ??
      null;

    const riskBasket = _findBasket(riskCandidateId, basketList);
    setRiskSelectedBasket(riskBasket);

    // ---- tenure ----
    const tenureCandidateId =
      mappingData.recommended_tenure_basket ??
      mappingData.tenure_baskets?.[0] ??
      null;

    const tenureBasket = _findBasket(tenureCandidateId, basketList);
    setTenureSelectedBasket(tenureBasket);
  };

  const _getBaskets = () => {
    APIService(false, "manage-basket/list")
      .then((res) => {
        if (res.status_code === 200 && Array.isArray(res.data)) {
          const mapped = res.data.map(mapApiToOption);

          // Keep ref current so _getMapping callbacks always see the latest list
          basketsRef.current = mapped;
          setBaskets(mapped);

          // If _getMapping already resolved while baskets were loading, apply now
          if (pendingMappingRef.current) {
            _applyMapping(pendingMappingRef.current, mapped);
            pendingMappingRef.current = null;
          }
        }
      })
      .catch(() => {
        setBaskets([]);
      });
  };

  /* ----------------------------------------
      API – list-mapping  (preselect baskets)
      Called on mount and on every tab switch.
  ---------------------------------------- */
  const _getMapping = useCallback((investmentStyle, tenure) => {
    setMappingLoading(true);
    // Clear previous selections while the new mapping loads
    setRiskSelectedBasket(null);
    setTenureSelectedBasket(null);
    setRiskSearchQuery("");
    setTenureSearchQuery("");

    const payload = { investment_style: investmentStyle, tenure };

    APIService(true, "manage-basket/list-mapping", payload)
      .then((res) => {
        if (res.status_code === 200 && res.data) {
          if (basketsRef.current.length > 0) {
            _applyMapping(res.data, basketsRef.current);
          } else {
            // Baskets haven't loaded yet — queue the mapping data
            pendingMappingRef.current = res.data;
          }
        }
      })
      .catch((err) => {
        console.error("Error fetching basket mapping:", err);
      })
      .finally(() => setMappingLoading(false));
  }, []);

  /* ----------------------------------------
      API – update-mapping (save)
  ---------------------------------------- */
  const _updateMapping = () => {
    let valid = true;

    if (!riskSelectedBasket?.id) {
      setRiskSelectedBasketError("Select a Risk category Basket");
      valid = false;
    }
    if (!tenureSelectedBasket?.id) {
      setTenureSelectedBasketError("Select a Tenure Basket");
      valid = false;
    }
    if (!valid) return;

    setSubmitLoading(true);

    const payload = {
      investment_style_id: activeRiskTab?.id,
      tenure_id: activeTenureTab?.id,
      risk_baskets: [riskSelectedBasket.id],
      recommended_risk_basket: riskSelectedBasket.id,
      tenure_baskets: [tenureSelectedBasket.id],
      recommended_tenure_basket: tenureSelectedBasket.id,
    };

    APIService(true, "manage-basket/update-mapping", payload)
      .then((res) => {
        if (res.status_code === 200) {
          toast.success(res.message || "Basket mapping updated successfully");
        } else {
          toast.error(res.message || "Failed to update basket mapping");
        }
      })
      .catch((err) => {
        toast.error(err?.message || "Something went wrong");
        console.error("Error updating basket mapping:", err);
      })
      .finally(() => setSubmitLoading(false));
  };

  /* ----------------------------------------
      FILTERING
  ---------------------------------------- */
  const riskFilteredOptions = useMemo(() => {
    const q = riskSearchQuery.trim().toLowerCase();
    if (!q) return [];
    return baskets.filter((item) =>
      (item.title || "").toLowerCase().includes(q),
    );
  }, [riskSearchQuery, baskets]);

  const tenureFilteredOptions = useMemo(() => {
    const q = tenureSearchQuery.trim().toLowerCase();
    if (!q) return [];
    return baskets.filter((item) =>
      (item.title || "").toLowerCase().includes(q),
    );
  }, [tenureSearchQuery, baskets]);

  /* ----------------------------------------
      RENDER
  ---------------------------------------- */
  if (tabsLoading) return null;

  return (
    <div
      className="d-flex flex-column w-100 row-gap-3 position-relative"
      style={{ zIndex: 2 }}
    >
      <h4 className="fw-500 fs-18 text-eerie-black pt-24">
        Basket Configuration
      </h4>

      <div className="e-glass-card d-flex flex-column p-3 rounded-16 rg-20">
        {/* -------------------------------- RISK SECTION -------------------------------- */}
        <h5 className="mb-0 fw-500 fs-16 lh-18 text-eerie-black">
          Risk Category
        </h5>

        <Tabs
          bottomBorder={false}
          data={riskTabs}
          labelStyle="fw-500 fs-14 lh-20"
          defaultActive={riskTabs?.[0]?.id}
          onChange={(id) => {
            const found = riskTabs.find((t) => t.id === id);
            if (found) setActiveRiskTab(found);
          }}
        />

        {/* <div className="row">
          <div className="col-lg-9 col-12">
            <Input
              id="risk_basket"
              type="text"
              label="Choose basket"
              placeholder="Search and select basket"
              require={true}
              value={riskSearchQuery}
              error={riskSelectedBasketError}
              handleChange={(v) => {
                setRiskSearchQuery(v);
                setTenureSearchQuery("");
              }}
              className="w-100 px-26 pe-3 py-10"
              leadingAccessory={
                <span
                  className="e-input-infield-affix e-input-infield-affix--start"
                  aria-hidden
                >
                  <Icon icon="search" size="14" />
                </span>
              }
              trailingAccessory={
                riskSearchQuery ? (
                  <span
                    onClick={() => setRiskSearchQuery("")}
                    className="e-input-infield-affix e-input-infield-affix--end e-cursor-pointer"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setRiskSearchQuery("");
                      }
                    }}
                  >
                    <Icon icon="close" size="14" />
                  </span>
                ) : null
              }
            />
          </div>

          <div className="col-11">
            {riskFilteredOptions.length > 0 && (
              <SelectBasket
                options={riskFilteredOptions}
                value={riskSelectedBasket}
                onChange={(basket) => {
                  setRiskSelectedBasket(basket);
                  setRiskSearchQuery("");
                }}
              />
            )}

            {mappingLoading ? (
              <p className="fs-14 text-davys-gray">Loading mapping…</p>
            ) : (
              riskSelectedBasket && (
                <div className="row pt-3">
                  <div className="col-lg-6 col-12 position-relative">
                    <div
                      style={{ right: "1%", top: "-5%" }}
                      className="position-absolute"
                    >
                      <PrimaryButton
                        icon="close"
                        size="14"
                        ClassName="bg-alizarin-red text-white d-flex justify-content-center align-items-center p-1 w-25x h-25x rounded-circle"
                        onPress={() => setRiskSelectedBasket(null)}
                      />
                    </div>
                    <BasketCard data={riskSelectedBasket} />
                  </div>
                </div>
              )
            )}
          </div>
        </div> */}

        {/* -------------------------------- TENURE SECTION -------------------------------- */}
        <h5 className="mb-0 fw-500 fs-16 lh-18 text-eerie-black">
          Select Tenure
        </h5>

        <Tabs
          bottomBorder={false}
          data={tenureTabs}
          labelStyle="fw-500 fs-14 lh-20"
          defaultActive={tenureTabs?.[0]?.id}
          onChange={(id) => {
            const found = tenureTabs.find((t) => t.id === id);
            if (found) setActiveTenureTab(found);
          }}
        />

        <div className="row">
          <div className="col-lg-9 col-12">
            <Input
              id="tenure_basket"
              type="text"
              label="Choose basket"
              placeholder="Search and select basket"
              require={true}
              value={tenureSearchQuery}
              error={tenureSelectedBasketError}
              handleChange={(v) => {
                setTenureSearchQuery(v);
                setRiskSearchQuery("");
              }}
              className="w-100 px-26 pe-3 py-10"
              leadingAccessory={
                <span
                  className="e-input-infield-affix e-input-infield-affix--start"
                  aria-hidden
                >
                  <Icon icon="search" size="14" />
                </span>
              }
              trailingAccessory={
                tenureSearchQuery ? (
                  <span
                    onClick={() => setTenureSearchQuery("")}
                    className="e-input-infield-affix e-input-infield-affix--end e-cursor-pointer"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setTenureSearchQuery("");
                      }
                    }}
                  >
                    <Icon icon="close" size="14" />
                  </span>
                ) : null
              }
            />
          </div>

          <div className="col-11">
            {tenureFilteredOptions.length > 0 && (
              <SelectBasket
                options={tenureFilteredOptions}
                value={tenureSelectedBasket}
                onChange={(basket) => {
                  setTenureSelectedBasket(basket);
                  setTenureSearchQuery("");
                }}
              />
            )}

            {mappingLoading ? (
              <p className="fs-14 text-davys-gray">Loading mapping…</p>
            ) : (
              tenureSelectedBasket && (
                <div className="row pt-3">
                  <div className="col-lg-6 col-12 position-relative">
                    <div
                      style={{ right: "1%", top: "-5%" }}
                      className="position-absolute"
                    >
                      <PrimaryButton
                        icon="close"
                        size="14"
                        ClassName="bg-alizarin-red text-white d-flex justify-content-center align-items-center p-1 w-25x h-25x rounded-circle"
                        onPress={() => setTenureSelectedBasket(null)}
                      />
                    </div>
                    <BasketCard data={tenureSelectedBasket} />
                  </div>
                </div>
              )
            )}
          </div>
        </div>

        {/* -------------------------------- SAVE BUTTON -------------------------------- */}
        <div className="row">
          <div className="col-lg-6 col-12 d-flex justify-content-end pt-3">
            <PrimaryButton
              name={submitLoading ? "Saving…" : "Save"}
              ClassName="rounded-pill bg-oxford-blue py-3 text-white px-4 fs-16 fw-500"
              onPress={_updateMapping}
              disabled={submitLoading || mappingLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
}